import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import LandingQuiz from "@/components/LandingQuiz";
import {
    HeartHandshake, ShieldCheck, Handshake, LifeBuoy, BarChart3,
    Lock, ArrowRight, Scale, MessageSquareQuote, CheckCircle2,
    GraduationCap, Users2, CalendarCheck, ClipboardList,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1516841273335-e39b37888115?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwbWVkaWNhbCUyMHN0YWZmfGVufDB8fHx8MTc4NTYxOTU5OXww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
    { icon: LifeBuoy, title: "Confidential Support", desc: "Raise concerns securely and get culturally competent advice within 24–72 hours based on urgency." },
    { icon: Handshake, title: "Early Mediation", desc: "Expert-led, early-stage conflict resolution that prevents grievances escalating into costly disputes." },
    { icon: Scale, title: "Fair Case Handling", desc: "Transparent digital case documentation and audit trail for consistent, evidence-led outcomes." },
    { icon: BarChart3, title: "WRES Insight", desc: "Workforce ethnicity and Workforce Race Equality Standard dashboards to drive measurable change." },
    { icon: MessageSquareQuote, title: "People Stories", desc: "A moderated space for BAME staff to share lived experience and build collective understanding." },
    { icon: Lock, title: "GDPR Secure", desc: "Encrypted, consent-driven and aligned to NHS cyber and data protection standards." },
];

const STATS = [
    { value: "£2.8bn", label: "Est. annual NHS cost of workplace conflict" },
    { value: "24–72h", label: "Triaged response by urgency" },
    { value: "9 in 10", label: "Cases resolvable before formal process" },
];

const SERVICES = [
    { icon: LifeBuoy, title: "Confidential Support", desc: "A 1:1 with a culturally competent adviser via secure chat, phone or video.", cta: "Book support" },
    { icon: Handshake, title: "Early Mediation", desc: "Expert-led, impartial mediation to resolve conflict before it escalates.", cta: "Request mediation" },
    { icon: GraduationCap, title: "Coaching", desc: "Accredited coaching to navigate challenges and build confidence at work.", cta: "Book coaching" },
    { icon: Users2, title: "Reverse Mentoring", desc: "Structured two-way learning pairing senior leaders with BAME colleagues.", cta: "Join programme" },
];

export default function Landing() {
    return (
        <div className="min-h-screen bg-white" data-testid="landing-page">
            {/* Nav */}
            <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <HeartHandshake className="h-6 w-6 text-nhs-blue" />
                        <span className="font-heading font-extrabold text-lg text-nhs-ink">EquitySupport</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/login"><Button variant="ghost" className="text-nhs-ink" data-testid="nav-login-button">Sign in</Button></Link>
                        <Link to="/register"><Button className="bg-nhs-blue hover:bg-nhs-darkblue text-white rounded-full px-5" data-testid="nav-register-button">Get started</Button></Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
                <div className="animate-fade-up">
                    <span className="inline-flex items-center gap-2 rounded-full bg-nhs-teal/10 text-nhs-teal px-4 py-1.5 text-sm font-semibold mb-6">
                        <ShieldCheck className="h-4 w-4" /> EDI-managed support for NHS BAME staff
                    </span>
                    <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-nhs-ink leading-[1.1]">
                        Reduce the human and financial cost of workplace conflict.
                    </h1>
                    <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
                        A culturally competent digital platform giving Black, Asian and Minority Ethnic staff confidential support, early mediation and a fair route to resolution — while giving leaders the insight to act.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-4">
                        <Link to="/register"><Button size="lg" className="bg-nhs-blue hover:bg-nhs-darkblue text-white rounded-full px-8 gap-2" data-testid="hero-get-started">Access support <ArrowRight className="h-4 w-4" /></Button></Link>
                        <a href="#pulse"><Button size="lg" variant="outline" className="rounded-full px-8 border-slate-300 gap-2" data-testid="hero-take-pulse"><ClipboardList className="h-4 w-4" /> Take the 2-min pulse</Button></a>
                    </div>
                    <div className="mt-8 flex items-center gap-2 text-sm text-slate-500">
                        <Lock className="h-4 w-4 text-nhs-teal" /> Confidential · GDPR compliant · Aligned to WRES & Freedom to Speak Up
                    </div>
                </div>
                <div className="relative animate-fade-up" style={{ animationDelay: "0.1s" }}>
                    <div className="absolute -inset-4 bg-nhs-blue/5 rounded-3xl -z-10" />
                    <img src={HERO} alt="Diverse NHS medical staff walking together" className="rounded-2xl shadow-lg w-full h-[420px] object-cover" data-testid="hero-image" />
                    <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg border border-slate-200 p-4 w-56 hidden sm:block">
                        <div className="flex items-center gap-2 text-nhs-teal font-semibold text-sm"><CheckCircle2 className="h-4 w-4" /> Case triaged</div>
                        <p className="text-xs text-slate-500 mt-1">Confidential support assigned to a culturally competent adviser.</p>
                    </div>
                </div>
            </section>

            {/* Stats */}
            <section className="bg-nhs-ink text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid sm:grid-cols-3 gap-8">
                    {STATS.map((s) => (
                        <div key={s.label} data-testid="stat-item">
                            <p className="font-heading text-4xl font-extrabold text-nhs-teal">{s.value}</p>
                            <p className="mt-2 text-slate-300 text-sm">{s.label}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Features */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                <div className="max-w-2xl">
                    <h2 className="font-heading text-3xl font-bold text-nhs-ink">Everything needed for early, fair resolution</h2>
                    <p className="mt-4 text-slate-600">One secure platform connecting staff, HR, leadership, unions and staff networks around better outcomes.</p>
                </div>
                <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {FEATURES.map((f) => (
                        <div key={f.title} className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 hover:-translate-y-1 hover:shadow-md transition-transform duration-200" data-testid="feature-card">
                            <div className="h-11 w-11 rounded-lg bg-nhs-blue/10 flex items-center justify-center mb-4">
                                <f.icon className="h-5 w-5 text-nhs-blue" />
                            </div>
                            <h3 className="font-heading font-semibold text-lg text-nhs-ink">{f.title}</h3>
                            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Services to book */}
            <section className="bg-[#F4F6F8] border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                    <div className="max-w-2xl">
                        <span className="inline-flex items-center gap-2 rounded-full bg-nhs-blue/10 text-nhs-blue px-4 py-1.5 text-sm font-semibold mb-4">
                            <CalendarCheck className="h-4 w-4" /> Services you can book
                        </span>
                        <h2 className="font-heading text-3xl font-bold text-nhs-ink">Support, on your terms</h2>
                        <p className="mt-4 text-slate-600">Book confidential, culturally competent services directly through the platform.</p>
                    </div>
                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {SERVICES.map((s) => (
                            <div key={s.title} className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 flex flex-col hover:-translate-y-1 hover:shadow-md transition-transform duration-200" data-testid="service-card">
                                <div className="h-11 w-11 rounded-lg bg-nhs-teal/10 flex items-center justify-center mb-4">
                                    <s.icon className="h-5 w-5 text-nhs-teal" />
                                </div>
                                <h3 className="font-heading font-semibold text-lg text-nhs-ink">{s.title}</h3>
                                <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{s.desc}</p>
                                <Link to="/register" className="mt-4">
                                    <Button variant="outline" className="w-full border-slate-300 hover:border-nhs-blue hover:text-nhs-blue gap-2" data-testid={`book-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
                                        {s.cta} <ArrowRight className="h-4 w-4" />
                                    </Button>
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Anonymous culture pulse quiz */}
            <LandingQuiz />

            {/* CTA */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
                <div className="rounded-2xl bg-nhs-blue px-8 py-14 text-center text-white">
                    <h2 className="font-heading text-3xl font-bold">Ready to build a psychologically safer workplace?</h2>
                    <p className="mt-3 text-blue-100 max-w-xl mx-auto">Join your Trust's confidential support platform and access help today.</p>
                    <Link to="/register"><Button size="lg" className="mt-8 bg-white text-nhs-blue hover:bg-slate-100 rounded-full px-8" data-testid="cta-register">Create your account</Button></Link>
                </div>
            </section>

            <footer className="border-t border-slate-200 py-8 text-center text-sm text-slate-500">
                EquitySupport · Confidential EDI-managed support for NHS BAME staff · GDPR compliant
            </footer>
        </div>
    );
}
